/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react';
import { Box, Flex, Text, Image, Icon, Avatar } from '@chakra-ui/react';
import { VscError } from 'react-icons/vsc';
import { AiOutlineAudio, AiOutlineCheckCircle } from 'react-icons/ai';
import { GoProject } from 'react-icons/go';
import { MdOndemandVideo } from 'react-icons/md';
import { CgFileDocument } from 'react-icons/cg';
import moment from 'moment';
import { ContentState, EditorState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import { handleImgError } from './HandleFunctions';

export const getCellExclusive = v => {
  if (v === true) {
    return <AiOutlineCheckCircle color="green" fontSize={25} />;
  }
  return <VscError color="red" fontSize={25} />;
};

export const toCurrency = (amount: number) => {
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Math.abs(amount));

  return formatted;
};

export const formatCurrency = (amount: number) => {
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);

  return formatted;
};

export const toNumber = (v: number | string | undefined | null): number => {
  if (v === undefined || v === null) return 0;
  if (typeof v === 'number') return v;
  const parsed = parseFloat(v);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getCellStatus = v => {
  if (v === 1 || v === true) {
    return (
      <span
        style={{ color: 'white', padding: 4, background: 'green', borderRadius: 5, fontSize: 14 }}
      >
        Ativo
      </span>
    );
  }
  return (
    <span style={{ color: 'white', padding: 4, background: 'red', borderRadius: 5, fontSize: 14 }}>
      Inativo
    </span>
  );
};

export const getCellColor = (value: any) => {
  if (value)
    return (
      <Flex align="center">
        <Box bg={value} borderRadius="5px" w="20px" h="20px" mr="5px" />
        <Text>{value}</Text>
      </Flex>
    );
  return 'Não informado';
};

export const getCellImage = (value: any, useAvatar = false) => {
  return (
    <>
      {useAvatar ? (
        <Avatar size="lg" src={value?.medium ?? ''} boxShadow="0 2px 4px #00000050" />
      ) : (
        <Image
          w="50px"
          borderRadius="5px"
          onError={handleImgError}
          src={value?.medium ?? ''}
          boxShadow="0 2px 4px #00000050"
        />
      )}
    </>
  );
};

export const getFileTypeIcon = (value: any) => {
  if (value === 'video')
    return (
      <Flex align="center">
        <Icon as={MdOndemandVideo} mr="5px" fontSize="25px" />
        <Text>Vídeo</Text>
      </Flex>
    );
  if (value === 'audio')
    return (
      <Flex align="center">
        <Icon as={AiOutlineAudio} mr="5px" fontSize="25px" />
        <Text>Audio</Text>
      </Flex>
    );
  if (value === 'docs')
    return (
      <Flex align="center">
        <Icon as={CgFileDocument} mr="5px" fontSize="25px" />
        <Text>Documento</Text>
      </Flex>
    );
  return 'Não informado';
};

export const getStatusDot = (value: any) => {
  if (value.length > 0)
    return [
      ...(value?.map(item => (
        <Flex align="center">
          <Icon as={GoProject} mr="5px" color={item.status ? 'green' : 'red'} fontSize="15px" />
          <Text>{item.name}</Text>
        </Flex>
      )) ?? []),
    ];
  return 'Não informado';
};

export const getArrayItems = (value: any) => {
  return <Text>{value.map(item => item.name).join(', ')}</Text>;
};

export const getAttachmentType = type => {
  switch (type) {
    case 1:
      return 'IMAGEM';
    case 2:
      return 'WORD';
    case 3:
      return 'PDF';
    case 4:
      return 'EXCEL';
    case 5:
      return 'POWER POINT';
    case 6:
      return 'ÁUDIO';
    case 7:
      return 'VÍDEO';
    default:
      return 'DESCONHECIDO';
  }
};

export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (format === 'ISO') return moment(date).toISOString();
  return moment(date).format(format);
};
export const getHtmlToDraftBlocks = html => {
  const blocksFromHtml = htmlToDraft(html);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  const editorState = EditorState.createWithContent(contentState);
  return editorState;
};

export const getFormattedLabel = (item, label: string | null = null) => {
  return {
    value: item.id,
    label: label ?? item.name ?? item.id,
  };
};

// CPF/CNPJ moram em `documento.ts` — funcoes puras, sem React nem Chakra, e
// por isso testaveis. Reexportadas aqui porque e daqui que os ~99 pontos do
// app ja as importam.
export { formatCPF, formatCNPJ, formatCPFOrCNPJ, getDocumentType } from './documento';

export const formatIntermunicipal = (value: number | null) => {
  switch (value) {
    case 0:
      return 'Não identificado';
    case 1:
      return 'Sim';
    case 2:
      return 'Não';
    default:
      return 'Não identificado';
  }
};
export const getFormattedLabelString = (item: string[] | undefined) => {
  if (!item) return [];

  return item.map(city => ({
    label: city,
    value: city,
  }));
};

// Formata nome de município para Title Case (ex: "BELO HORIZONTE" -> "Belo Horizonte")
export const formatMunicipio = (nome: string | undefined | null): string => {
  if (!nome) return '';
  return nome
    .toLowerCase()
    .split(' ')
    .map(word => {
      // Palavras que devem ficar em minúsculo (preposições)
      const lowerWords = ['de', 'da', 'do', 'das', 'dos', 'e'];
      if (lowerWords.includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * Remove formatação de documentos (CPF/CNPJ)
 * Remove caracteres como '.', '/', '-' e espaços
 * Exemplo: "12.345.678/0001-90" -> "12345678000190"
 */
export const sanitizeDocument = (value: string | number | undefined | null): string => {
  if (!value) return '';
  return value.toString().replace(/\D/g, '');
};

/**
 * Normaliza valor monetário para comparação
 * Aceita formatos brasileiro e americano, com ou sem separadores de milhar
 * Exemplos:
 *   "1.500,00" -> "1500.00" (brasileiro com milhar)
 *   "1500,00"  -> "1500.00" (brasileiro sem milhar)
 *   "1.500"    -> "1500"    (brasileiro só milhar)
 *   "1,500.00" -> "1500.00" (americano com milhar)
 *   "1500.00"  -> "1500.00" (americano/técnico)
 *   "1500"     -> "1500"    (sem formatação)
 */
export const normalizeMonetaryValue = (value: string | undefined | null): string => {
  if (!value) return '';

  const hasComma = value.includes(',');
  const hasDot = value.includes('.');

  let normalized = value;

  if (hasComma && hasDot) {
    // Tem ambos - determinar qual é decimal pelo último separador
    const lastCommaIndex = value.lastIndexOf(',');
    const lastDotIndex = value.lastIndexOf('.');

    if (lastCommaIndex > lastDotIndex) {
      // Formato brasileiro: 1.500,00 -> pontos são milhares, vírgula é decimal
      normalized = value.replace(/\./g, '').replace(',', '.');
    } else {
      // Formato americano: 1,500.00 -> vírgulas são milhares, ponto é decimal
      normalized = value.replace(/,/g, '');
    }
  } else if (hasComma) {
    // Só vírgula - assumir formato brasileiro (vírgula é decimal)
    normalized = value.replace(',', '.');
  } else if (hasDot) {
    // Só ponto - verificar se é milhar ou decimal
    const parts = value.split('.');
    const lastPart = parts[parts.length - 1];
    // Se a parte após o ponto tem 3 dígitos e há mais partes, é milhar
    // Ex: "1.500" (milhar) vs "15.00" (decimal)
    if (parts.length > 1 && lastPart.length === 3 && /^\d+$/.test(lastPart)) {
      // Provavelmente é separador de milhar brasileiro
      normalized = value.replace(/\./g, '');
    }
    // Caso contrário, manter como está (é decimal)
  }

  // Remove ponto final se não houver decimais depois
  if (normalized.endsWith('.')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
};
